import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { localizeVehicleModel } from '../utils/localization';

const KAKAO_CHAT_URL = 'http://pf.kakao.com/_UFxkBl/chat';

interface VipVehicle {
  categoryKo: string;
  categoryEn: string;
  name: string;
  seats: number;
  image: string;
  oneWay: string;
  nineHours: string;
}

const vehicles: VipVehicle[] = [
  { categoryKo: '승합', categoryEn: 'Van', name: '카니발 4세대(9인승)', seats: 9, image: 'VG1UsDHQbFhNg8nl.png', oneWay: '120,000', nineHours: '350,000' },
  { categoryKo: '승합', categoryEn: 'Van', name: '카니발 리무진 4세대(7인승)', seats: 7, image: 'XlWv7Sny9Lgj2eq8.png', oneWay: '120,000', nineHours: '380,000' },
  { categoryKo: '승합', categoryEn: 'Van', name: 'All New 카니발(9인승)', seats: 9, image: 'XEdAQMJGhiHP32Oe.png', oneWay: '110,000', nineHours: '300,000' },
  { categoryKo: '승합', categoryEn: 'Van', name: '카니발 하이리무진', seats: 9, image: 'OqSKokm4Mxnyrs1X.png', oneWay: '150,000', nineHours: '400,000' },
  { categoryKo: '승합', categoryEn: 'Van', name: '그랜드스타렉스(11인승)', seats: 11, image: 'kAgcWfTPqHJSsY89.png', oneWay: '110,000', nineHours: '300,000' },
  { categoryKo: '승합', categoryEn: 'Van', name: '스타리아(11인승)', seats: 11, image: '8PEoLCaIv124fMZc.png', oneWay: '120,000', nineHours: '350,000' },
  { categoryKo: '승합', categoryEn: 'Van', name: '솔라티(15인승)', seats: 15, image: 'SxLAfrd2v0ybMDWh.png', oneWay: '200,000', nineHours: '400,000' },
  { categoryKo: '승용', categoryEn: 'Sedan', name: '쏘나타 DN8', seats: 5, image: '7haWyP032rlKOLM5.png', oneWay: '110,000', nineHours: '250,000' },
  { categoryKo: '승용', categoryEn: 'Sedan', name: '그랜져 GN7', seats: 5, image: 'qD1p0NAwricBFbZM.png', oneWay: '110,000', nineHours: '300,000' },
  { categoryKo: '승용', categoryEn: 'Sedan', name: '제네시스 G80', seats: 5, image: 'Qrk7SzsR8qndTy9Y.png', oneWay: '120,000', nineHours: '350,000' },
  { categoryKo: '승용', categoryEn: 'Sedan', name: '벤츠 S450', seats: 5, image: 'I2yvAGsnLKQFhwPp.png', oneWay: '상담문의', nineHours: '500,000' },
  { categoryKo: '버스', categoryEn: 'Bus', name: '미니버스(25인승)', seats: 25, image: 'SZzGF8OrsxfhoAcE.png', oneWay: '상담문의', nineHours: '상담문의' },
  { categoryKo: '버스', categoryEn: 'Bus', name: '대형버스(45인승)', seats: 45, image: 'vn7UZPc3hbp0WrHD.png', oneWay: '상담문의', nineHours: '상담문의' },
  { categoryKo: '버스', categoryEn: 'Bus', name: '리무진버스(28인승)', seats: 28, image: 'h19q8LiUvemZH4ty.png', oneWay: '상담문의', nineHours: '상담문의' },
];

const VipPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language.startsWith('en');

  const displayPrice = (price: string) => {
    if (price === '상담문의') return isEnglish ? 'Contact us' : '상담문의';
    return isEnglish ? `${price} KRW` : `￦${price}`;
  };

  const displayName = (name: string) => {
    if (!isEnglish) return name;
    return localizeVehicleModel(name, i18n.language)
      .replace('카니발 High Limousine', 'Carnival Hi Limousine')
      .replace('솔라티', 'Solati')
      .replace('벤츠', 'Mercedes-Benz')
      .replace('미니버스', 'Minibus')
      .replace('대형버스', 'Large Bus')
      .replace('리무진버스', 'Limousine Bus');
  };

  return (
    <>
      <Helmet>
        <title>{isEnglish ? 'Chauffeur & Tour | Goodtime Rent Car' : '의전&투어 | 굿타임 렌터카'}</title>
        <meta
          name="description"
          content={isEnglish ? 'Chauffeur vehicles, private tours, vans, sedans, and charter bus service in Busan.' : '부산 의전&투어, 승합차, 승용차, 전세버스 요금 안내'}
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header />

        <main>
          <section className="bg-gradient-to-r from-slate-950 via-slate-800 to-blue-900 text-white">
            <div className="max-w-7xl mx-auto px-4 py-14 md:py-20">
              <div className="max-w-3xl">
                <h1 className="text-4xl md:text-5xl font-bold mb-5">{t('vip.title')}</h1>
                <p className="text-lg text-white/80 leading-relaxed">{t('vip.subtitle')}</p>
              </div>
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-4 py-10 md:py-14">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{t('vip.vehicleList')}</h2>
                <p className="text-gray-600 mt-2">{t('vip.vehicleListDesc')}</p>
              </div>
              <a
                href="tel:1566-4412"
                className="inline-flex items-center justify-center rounded-lg bg-slate-900 px-5 py-3 text-sm font-bold text-white hover:bg-slate-800"
              >
                {t('vip.callVip')} 1566-4412
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {vehicles.map((vehicle) => (
                <article key={vehicle.name} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100">
                  <div className="aspect-[16/10] bg-gray-100">
                    <img
                      src={`/assets/vip/${vehicle.image}`}
                      alt={displayName(vehicle.name)}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">
                        {isEnglish ? vehicle.categoryEn : vehicle.categoryKo}
                      </span>
                      <span className="text-sm font-semibold text-gray-600">
                        {vehicle.seats}{isEnglish ? ' seats' : '인승'} · {t('vip.driverIncluded')}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-5">{displayName(vehicle.name)}</h3>

                    <div className="rounded-lg border border-gray-200 overflow-hidden">
                      <div className="grid grid-cols-3 bg-gray-50 text-xs font-bold text-gray-600">
                        <div className="px-3 py-2">{t('vip.route')}</div>
                        <div className="px-3 py-2">{t('vip.duration')}</div>
                        <div className="px-3 py-2 text-right">{t('vip.price')}</div>
                      </div>
                      <div className="grid grid-cols-3 text-sm border-t border-gray-200">
                        <div className="px-3 py-3 text-gray-700">{t('vip.busanCity')}</div>
                        <div className="px-3 py-3 text-gray-700">{t('vip.oneWay')}</div>
                        <div className="px-3 py-3 text-right font-bold text-gray-900">{displayPrice(vehicle.oneWay)}</div>
                      </div>
                      <div className="grid grid-cols-3 text-sm border-t border-gray-200">
                        <div className="px-3 py-3 text-gray-700">{t('vip.busanCity')}</div>
                        <div className="px-3 py-3 text-gray-700">9{isEnglish ? ' hours' : '시간'}</div>
                        <div className="px-3 py-3 text-right font-bold text-gray-900">{displayPrice(vehicle.nineHours)}</div>
                      </div>
                    </div>

                    <a
                      href={KAKAO_CHAT_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 flex w-full min-h-12 items-center justify-center rounded-lg bg-blue-600 px-4 py-3 text-base font-bold text-white hover:bg-blue-700 transition-colors shadow-sm"
                    >
                      {t('vip.liveChat')}
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default VipPage;
